// topic.jsx — a live Sefaria topic explorer. Descriptions, source counts, and
// references are rendered only when the upstream topic record provides them.
const { useState: useStateT, useMemo: useMemoT, useEffect: useEffectT } = React;

function topicSourceType(ref) {
  const value = String(ref || "");
  if (/Genesis|Exodus|Leviticus|Numbers|Deuteronomy|Psalms|Isaiah|Proverbs/i.test(value)) return "Tanach";
  if (/Mishnah|Avot/i.test(value)) return "Mishnah";
  if (/Berakhot|Shabbat|Talmud|Bava|Sanhedrin|Sukkah|Yoma/i.test(value)) return "Gemara";
  if (/Midrash|Rabbah|Tanchuma|Mechilta|Sifrei/i.test(value)) return "Midrash";
  if (/Tanya|Torah Or|Likkutei Torah|Chassid|Maamar|Sichos/i.test(value)) return "Chassidus";
  return "Other";
}

function TopicRefList({ items, onOpenReader }) {
  if (!items.length) return <div className="cmd-empty">No indexed text references were supplied for this topic.</div>;
  return <div className="sicha-list">{items.map((item, index) => (
    <button key={`${item.ref}-${index}`} className="sicha-item" onClick={() => onOpenReader && onOpenReader(item.ref)}>
      <Icon d="book" size={15} style={{ color: "var(--accent)", flex: "none" }} />
      <span className="sicha-txt"><span className="sicha-en">{item.title || item.ref}</span><i>{item.excerpt || item.ref}</i></span>
      <span className="sicha-open">Open</span>
    </button>
  ))}</div>;
}

function TopicPage({ approach, conceptId, onApproach, onConcept, onOpenReader }) {
  const relatedTopics = useMemoT(() => {
    const resources = (window.RELATED_RESOURCES || []).filter((resource) => resource.kind === "topic");
    const seen = new Set();
    return resources.map((resource) => ({
      slug: resource.id || resource.slug,
      title: resource.title || resource.en,
      heTitle: resource.heTitle || resource.he || "",
      count: resource.count,
      origin: "current",
    })).filter((topic) => topic.slug && topic.title && !seen.has(topic.slug) && seen.add(topic.slug));
  }, []);
  const [query, setQuery] = useStateT("");
  const [topics, setTopics] = useStateT(() => {
    const initial = (window.TOPICS || []).map((topic) => ({ slug: topic.slug || topic.id, title: topic.en || topic.title, heTitle: topic.he || topic.heTitle, count: topic.count }));
    const merged = new Map([...relatedTopics, ...initial].filter((topic) => topic.slug).map((topic) => [topic.slug, topic]));
    relatedTopics.forEach((topic) => merged.set(topic.slug, topic));
    return [...merged.values()];
  });
  const [topicScope, setTopicScope] = useStateT(relatedTopics.length ? "current" : "all");
  const [sourceType, setSourceType] = useStateT("All");
  const [detail, setDetail] = useStateT(null);
  const [loading, setLoading] = useStateT(false);
  const selectedSlug = conceptId || "";

  useEffectT(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      fetch(`/bff/topics?q=${encodeURIComponent(query.trim())}&limit=30`, { signal: controller.signal })
        .then((response) => response.ok ? response.json() : { topics: [] })
        .then((data) => {
          const liveTopics = Array.isArray(data.topics) ? data.topics : [];
          const merged = new Map();
          relatedTopics.forEach((topic) => merged.set(topic.slug, topic));
          liveTopics.forEach((topic) => merged.set(topic.slug, Object.assign({}, merged.get(topic.slug) || {}, topic)));
          setTopics([...merged.values()]);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, query ? 220 : 0);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [query]);

  useEffectT(() => {
    if (!selectedSlug) { setDetail(null); return; }
    setSourceType("All");
    let cancelled = false;
    setLoading(true);
    fetch(`/bff/topics/${encodeURIComponent(selectedSlug)}`)
      .then((response) => response.ok ? response.json() : null)
      .then((data) => { if (!cancelled) setDetail(data && !data.error ? data : null); })
      .catch(() => { if (!cancelled) setDetail(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedSlug]);

  const refs = useMemoT(() => {
    if (!detail) return [];
    const publicRefs = (detail.refs || []).filter((item) => !item.isSheet).map((item) => ({ ref: item.ref, title: item.ref, type: topicSourceType(item.ref) }));
    const custom = (detail.customHits || []).filter((item) => item.ref).map((item) => ({ ...item, type: topicSourceType(item.ref) }));
    const seen = new Set();
    return [...custom, ...publicRefs].filter((item) => item.ref && !seen.has(item.ref) && seen.add(item.ref)).slice(0, 120);
  }, [detail]);
  const sourceTypes = useMemoT(() => {
    const counts = {};
    refs.forEach((item) => { counts[item.type] = (counts[item.type] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [refs]);
  const visibleRefs = sourceType === "All" ? refs : refs.filter((item) => item.type === sourceType);
  const graphNodes = visibleRefs.map((item, index) => ({ id: `topic-${index}`, n: index + 1, type: item.type, title: item.title || item.ref, citation: item.ref, quoted: true }));
  const visibleTopics = query.trim() || topicScope === "all" ? topics : topics.filter((topic) => topic.origin === "current");

  return (
    <div className="topic">
      <div className="topic-bar">
        <form className="hero-search" onSubmit={(event) => event.preventDefault()} style={{ maxWidth: 520 }}>
          <Icon d="search" size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search real Sefaria topics…" />
        </form>
        <div className="approach-toggle" role="tablist" aria-label="Topic view">
          {[['essay', 'Sources', 'text'], ['atlas', 'Map', 'graph']].map(([key, label, icon]) => (
            <button key={key} className={`approach-btn${approach === key ? ' on' : ''}`} onClick={() => onApproach(key)}><Icon d={icon} size={14} /> {label}</button>
          ))}
        </div>
      </div>

      <div className="topic-discovery">
        <div className="topic-scope" role="tablist" aria-label="Topic discovery scope">
          {relatedTopics.length > 0 && <button role="tab" aria-selected={topicScope === "current"} className={topicScope === "current" ? "on" : ""} onClick={() => { setTopicScope("current"); setQuery(""); }}><Icon d="path" size={14} /> From this text <span>{relatedTopics.length}</span></button>}
          <button role="tab" aria-selected={topicScope === "all"} className={topicScope === "all" ? "on" : ""} onClick={() => setTopicScope("all")}><Icon d="compass" size={14} /> All live topics <span>{topics.length}</span></button>
        </div>
        <p>{topicScope === "current" && relatedTopics.length ? "Topics linked to the text you were reading. Open one to add Sefaria references and matching Mayanos passages dynamically." : "Browse Sefaria’s live topic index. Mayanos adds matching passages from this library when you open a topic."}</p>
      </div>

      <div className="ctabs">
        {visibleTopics.map((topic) => <button key={topic.slug} className={`ctab${selectedSlug === topic.slug ? ' on' : ''}`} onClick={() => onConcept(topic.slug)}>
          {topic.heTitle && <span lang="he" dir="rtl" className="heb ctab-he">{topic.heTitle}</span>}<span className="ctab-en">{topic.title}</span>
        </button>)}
        {!visibleTopics.length && <span className="topic-none">No topics are linked to this text yet. Switch to All live topics or search above.</span>}
      </div>

      {loading && !detail ? <div className="cmd-empty">Loading live topic data…</div> : !detail ? (
        <div className="essay"><header className="essay-hero"><div className="essay-kicker"><span className="dot" style={{ background: "var(--gold)" }} /> Live topic index</div><h1 className="serif essay-en">Choose or search a topic</h1><p className="essay-essence serif">Topic descriptions and references come directly from Sefaria; Mayanos adds matching passages from the custom Chassidus corpus.</p></header></div>
      ) : (
        <div className={approach === "atlas" ? "atlas" : "essay"}>
          <header className={approach === "atlas" ? "atlas-hero" : "essay-hero"}>
            {detail.heTitle && <span className={`heb ${approach === "atlas" ? "atlas-he" : "essay-he"}`}>{detail.heTitle}</span>}
            <h1 className={`serif ${approach === "atlas" ? "atlas-en" : "essay-en"}`}>{detail.title}</h1>
            {detail.description && <p className={approach === "atlas" ? "atlas-essence" : "essay-essence serif"}>{detail.description}</p>}
            {detail.heDescription && <p lang="he" dir="rtl" className="heb essay-def">{detail.heDescription}</p>}
            <div className="statstrip"><div className="stat"><b>{Number(detail.numSources || 0).toLocaleString()}</b><span>Sefaria sources</span></div><div className="stat"><b>{refs.length}</b><span>browseable matches</span></div><div className="stat"><b>{(detail.customHits || []).length}</b><span>Mayanos matches</span></div></div>
          </header>
          <div className="topic-source-filters" aria-label="Filter topic sources">
            <button className={sourceType === "All" ? "on" : ""} onClick={() => setSourceType("All")}>All <span>{refs.length}</span></button>
            {sourceTypes.map(([type, count]) => <button key={type} className={sourceType === type ? "on" : ""} onClick={() => setSourceType(type)}><TypeDot type={type} size={7} /> {type} <span>{count}</span></button>)}
          </div>
          {approach === "atlas" ? <section className="atlas-graph-panel"><div className="atlas-section-h"><Icon d="graph" size={16} /> Live source constellation</div>{graphNodes.length ? <SourceGraph sources={graphNodes} height={430} animate /> : <div className="cmd-empty">No text references were supplied for this topic.</div>}<TopicRefList items={visibleRefs} onOpenReader={onOpenReader} /></section>
            : <div className="essay-grid"><main className="essay-main"><section className="essay-block"><h2 className="essay-h">Texts connected to this topic</h2><TopicRefList items={visibleRefs} onOpenReader={onOpenReader} /></section></main><aside className="essay-rail"><div className="erail-card"><div className="erail-h">Built dynamically</div><p className="rail-hint">Topic metadata and public references come from Sefaria. Matching passages are added from the Mayanos corpus, then grouped here by source family.</p></div></aside></div>}
        </div>
      )}
    </div>
  );
}

Object.assign(window, { TopicPage });
